import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import type {
    BlogType,
    BlogPost,
    BlogPostsListResult,
    BlogPostsStats,
    BlogBatchResult,
} from '../types';
import type { BlogBatchImportItem } from '../lib/parseBlogPostFile';
import { BLOG_RETIRE_AND_REDIRECT_ENABLED, BLOG_RETIRE_DISABLED_MESSAGE } from '../lib/blogRetireFlag';

const BLOG_KEY = ['admin', 'blog'];

function asPayload<T>(data: unknown): T {
    const norm = normalizeRpcResponse(data);
    return (norm[0] ?? {}) as T;
}

export function useBlogTypes() {
    return useQuery<BlogType[]>({
        queryKey: [...BLOG_KEY, 'types'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_blog_types');
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as BlogType[];
        },
        staleTime: 30_000,
    });
}

export interface BlogPostsFilters {
    statusFilter: string;
    typeId: string | null;
    search: string;
    incompleteOnly?: boolean;
    limit?: number;
    offset?: number;
    /** NULL = todos; es|en filtra. */
    locale?: 'es' | 'en' | null;
}

export function useBlogPostsList(filters: BlogPostsFilters) {
    return useQuery<BlogPostsListResult>({
        queryKey: [...BLOG_KEY, 'posts-list', filters],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_blog_posts', {
                p_status_filter: filters.statusFilter || 'all',
                p_type_id: filters.typeId || null,
                p_search: filters.search.trim() || '',
                p_incomplete_only: Boolean(filters.incompleteOnly),
                p_limit: filters.limit ?? 24,
                p_offset: filters.offset ?? 0,
                p_locale: filters.locale ?? null,
            });
            if (error) throw new Error(error.message);
            const payload = asPayload<BlogPostsListResult>(data);
            return {
                items: payload.items ?? [],
                total: payload.total ?? 0,
                limit: payload.limit ?? 24,
                offset: payload.offset ?? 0,
            };
        },
        staleTime: 15_000,
    });
}

export function useBlogPostsStats() {
    return useQuery<BlogPostsStats>({
        queryKey: [...BLOG_KEY, 'posts-stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_blog_posts_stats');
            if (error) throw new Error(error.message);
            return asPayload<BlogPostsStats>(data);
        },
        staleTime: 15_000,
    });
}

export function useBlogPost(id: string | null) {
    return useQuery<BlogPost | null>({
        queryKey: [...BLOG_KEY, 'post', id],
        queryFn: async () => {
            if (!id) return null;
            const { data, error } = await dbClient.rpc('admin_get_blog_post_by_id', { p_id: id });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            return (rows[0] as BlogPost) ?? null;
        },
        enabled: !!id,
        staleTime: 15_000,
    });
}

export function useCreateBlogType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: { p_name: string; p_slug: string; p_color?: string; p_sort_order?: number }) => {
            const { data, error } = await dbClient.rpc('admin_create_blog_type', params);
            if (error) throw new Error(error.message);
            return data as string;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: BLOG_KEY }),
    });
}

export function useUpdateBlogType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { error } = await dbClient.rpc('admin_update_blog_type', params);
            if (error) throw new Error(error.message);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: BLOG_KEY }),
    });
}

export function useDeleteBlogType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { error } = await dbClient.rpc('admin_delete_blog_type', { p_id: id });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: BLOG_KEY }),
    });
}

export function useCreateBlogPost() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { data, error } = await dbClient.rpc('admin_create_blog_post', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            return String(rows[0] ?? data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: BLOG_KEY }),
    });
}

export function useUpdateBlogPost() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { error } = await dbClient.rpc('admin_update_blog_post', params);
            if (error) throw new Error(error.message);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: BLOG_KEY }),
    });
}

export function useToggleBlogPostStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data, error } = await dbClient.rpc('admin_toggle_blog_post_status', { p_id: id });
            if (error) throw new Error(error.message);
            return data as string;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: BLOG_KEY }),
    });
}

export interface RetireBlogPostResult {
    old_slug: string;
    new_slug: string;
    rewritten: number;
}

/** El detalle abierto en el editor no se recarga: pisaría cambios sin guardar. */
export function useRetireBlogPost() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: { p_id: string; p_to_slug: string; p_note: string | null }) => {
            if (!BLOG_RETIRE_AND_REDIRECT_ENABLED) {
                throw new Error(BLOG_RETIRE_DISABLED_MESSAGE);
            }
            const { data, error } = await dbClient.rpc('admin_retire_blog_post', params);
            if (error) throw new Error(error.message);
            return asPayload<RetireBlogPostResult>(data);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [...BLOG_KEY, 'posts-list'] });
            qc.invalidateQueries({ queryKey: [...BLOG_KEY, 'posts-stats'] });
            qc.invalidateQueries({ queryKey: [...BLOG_KEY, 'post'], refetchType: 'none' });
        },
    });
}

export function useDeleteBlogPost() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { error } = await dbClient.rpc('admin_delete_blog_post', { p_id: id });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: BLOG_KEY }),
    });
}

export function useUpsertBlogPostsBatch() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { items: BlogBatchImportItem[]; dryRun: boolean }) => {
            const { data, error } = await dbClient.rpc('admin_upsert_blog_posts_batch', {
                p_items: input.items,
                p_dry_run: input.dryRun,
            });
            if (error) throw new Error(error.message);
            return asPayload<BlogBatchResult>(data);
        },
        onSuccess: (_data, vars) => {
            if (vars.dryRun) return;
            qc.invalidateQueries({ queryKey: BLOG_KEY });
        },
    });
}
